/**
 * The health intake's questions and the shape of each answer (specs/product/intake.md). Answers
 * are stored as JSON with the question's version (INT-031), and read back through these schemas,
 * so an answer that no longer fits its question is treated as unanswered rather than guessed at.
 *
 * The question ids are stable. Rewording a question bumps its version and leaves old answers
 * meaning what they meant when given.
 */
import { z } from "zod";

const Day = z.iso.date();

export const QUESTIONS = {
  date_of_birth: { version: 1, answer: z.object({ date: Day }) },
  sex: { version: 1, answer: z.enum(["female", "male"]) },
  pregnancy: { version: 1, answer: z.boolean() },
  height_weight: {
    version: 1,
    answer: z.object({
      heightCm: z.number().min(100).max(250),
      weightKg: z.number().min(30).max(300),
    }),
  },
  conditions: { version: 1, answer: z.array(z.string().min(1)) },
  medications: { version: 1, answer: z.array(z.string().min(1)) },
  supplements: {
    version: 1,
    answer: z.array(
      z.object({
        name: z.string().min(1),
        ingredientId: z.string().nullable(),
        dailyDose: z.number().nullable(),
      }),
    ),
  },
  allergies: { version: 1, answer: z.array(z.string().min(1)) },
  diet: { version: 1, answer: z.enum(["omnivore", "pescatarian", "vegetarian", "vegan", "other"]) },
  exercise: {
    version: 1,
    answer: z.object({
      daysPerWeek: z.number().int().min(0).max(7),
      type: z.enum(["none", "strength", "endurance", "mixed", "other"]),
    }),
  },
  goal: { version: 1, answer: z.enum(["general", "energy", "strength", "weight", "other"]) },
} as const;

export type QuestionId = keyof typeof QUESTIONS;

export function isQuestionId(id: string): id is QuestionId {
  return Object.hasOwn(QUESTIONS, id);
}

/** Every answer a patient has given that still fits its question. */
export interface IntakeAnswers {
  readonly date_of_birth?: z.infer<typeof QUESTIONS.date_of_birth.answer>;
  readonly sex?: z.infer<typeof QUESTIONS.sex.answer>;
  readonly pregnancy?: z.infer<typeof QUESTIONS.pregnancy.answer>;
  readonly height_weight?: z.infer<typeof QUESTIONS.height_weight.answer>;
  readonly conditions?: z.infer<typeof QUESTIONS.conditions.answer>;
  readonly medications?: z.infer<typeof QUESTIONS.medications.answer>;
  readonly supplements?: z.infer<typeof QUESTIONS.supplements.answer>;
  readonly allergies?: z.infer<typeof QUESTIONS.allergies.answer>;
  readonly diet?: z.infer<typeof QUESTIONS.diet.answer>;
  readonly exercise?: z.infer<typeof QUESTIONS.exercise.answer>;
  readonly goal?: z.infer<typeof QUESTIONS.goal.answer>;
}

/** A schema that reads a JSON string and then checks the value against `schema`. */
function fromJson<T extends z.ZodType>(schema: T) {
  return z
    .string()
    .transform((text, ctx) => {
      try {
        return JSON.parse(text);
      } catch {
        ctx.addIssue({ code: "custom", message: "not JSON" });

        return z.NEVER;
      }
    })
    .pipe(schema);
}

/** Whether an answer fits its question as it stands. */
export function fitsQuestion(question: QuestionId, answerJson: string): boolean {
  return fromJson(QUESTIONS[question].answer).safeParse(answerJson).success;
}

/** True when two stored answers are the same value, ignoring JSON key order. */
export function sameAnswerJson(left: string, right: string): boolean {
  try {
    return JSON.stringify(JSON.parse(left)) === JSON.stringify(JSON.parse(right));
  } catch {
    return left === right;
  }
}

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

/** One reader per question, each assigning only what its own schema accepted. */
type Reader = (out: Mutable<IntakeAnswers>, text: string) => void;

const READERS = {
  date_of_birth: (out, text) => {
    const read = fromJson(QUESTIONS.date_of_birth.answer).safeParse(text);

    if (read.success) out.date_of_birth = read.data;
  },
  sex: (out, text) => {
    const read = fromJson(QUESTIONS.sex.answer).safeParse(text);

    if (read.success) out.sex = read.data;
  },
  pregnancy: (out, text) => {
    const read = fromJson(QUESTIONS.pregnancy.answer).safeParse(text);

    if (read.success) out.pregnancy = read.data;
  },
  height_weight: (out, text) => {
    const read = fromJson(QUESTIONS.height_weight.answer).safeParse(text);

    if (read.success) out.height_weight = read.data;
  },
  conditions: (out, text) => {
    const read = fromJson(QUESTIONS.conditions.answer).safeParse(text);

    if (read.success) out.conditions = read.data;
  },
  medications: (out, text) => {
    const read = fromJson(QUESTIONS.medications.answer).safeParse(text);

    if (read.success) out.medications = read.data;
  },
  supplements: (out, text) => {
    const read = fromJson(QUESTIONS.supplements.answer).safeParse(text);

    if (read.success) out.supplements = read.data;
  },
  allergies: (out, text) => {
    const read = fromJson(QUESTIONS.allergies.answer).safeParse(text);

    if (read.success) out.allergies = read.data;
  },
  diet: (out, text) => {
    const read = fromJson(QUESTIONS.diet.answer).safeParse(text);

    if (read.success) out.diet = read.data;
  },
  exercise: (out, text) => {
    const read = fromJson(QUESTIONS.exercise.answer).safeParse(text);

    if (read.success) out.exercise = read.data;
  },
  goal: (out, text) => {
    const read = fromJson(QUESTIONS.goal.answer).safeParse(text);

    if (read.success) out.goal = read.data;
  },
} satisfies { readonly [Q in QuestionId]: Reader };

/**
 * Stored answers, oldest first, read into one object. A later answer replaces an earlier one, and
 * an answer that no longer fits its question is skipped.
 */
export function readIntake(
  rows: readonly { readonly questionId: string; readonly answerJson: string }[],
): IntakeAnswers {
  const out: Mutable<IntakeAnswers> = {};

  for (const row of rows)
    if (isQuestionId(row.questionId)) READERS[row.questionId](out, row.answerJson);

  return out;
}

/** Questions every draft needs. The rest default to "none given". */
export const REQUIRED: readonly QuestionId[] = [
  "date_of_birth",
  "sex",
  "height_weight",
  "exercise",
  "diet",
  "goal",
];

/** Whole years between a birth date and a day, both ISO dates. */
export function ageOn(dateOfBirth: string, day: string): number {
  const [by = 0, bm = 0, bd = 0] = dateOfBirth.split("-").map(Number);
  const [y = 0, m = 0, d = 0] = day.split("-").map(Number);

  return y - by - (m < bm || (m === bm && d < bd) ? 1 : 0);
}
